import {
  getLocalTimeZone,
  now,
  type ZonedDateTime,
} from '@internationalized/date';
import { PriceListType } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import type { SelectedCell } from '../_components/_shared/hooks/useProductPricingColumns';
import {
  customPriceListArchiveAtom,
  customPriceListDeleteAtom,
  customPriceListPublishAtom,
  customPriceListUnarchiveAtom,
} from '../_graphql/mutationCustomPriceListLifecycle';
import { customPriceListsListQueryAtom } from '../_graphql/queryCustomPriceListsList';
import {
  EvaluatedPricesMap,
  evaluateFormulasQueryAtom,
} from '../_graphql/queryEvaluateFormulas';
import { productPricingsQueryAtom } from '../_graphql/queryProductPricings';

import { atomFromRoutePath } from '~/shared/state';

export const $customPriceListDetailPathParams = atomFromRoutePath(
  '/pricing/lists/:id' as const,
);

export const $customPriceListId = atom((get) =>
  get($customPriceListDetailPathParams)?.id
    ? String(get($customPriceListDetailPathParams)?.id)
    : null,
);

export const $remoteCustomPriceList = customPriceListsListQueryAtom((get) => {
  const customPriceListId = get($customPriceListId);
  return {
    variables: {
      ids: customPriceListId ? [customPriceListId] : undefined,
      limit: 1,
    },
    skip: !customPriceListId,
  };
});

export const $asOfDateTime = atom<ZonedDateTime | null>(null);

export const $remoteProductPricings = productPricingsQueryAtom((get) => {
  const asOfDateTime = get($asOfDateTime);
  const isHistorical =
    asOfDateTime && asOfDateTime.compare(now(getLocalTimeZone())) < 0;
  const asOfStr = isHistorical ? asOfDateTime.toAbsoluteString() : undefined;

  return {
    variables: {
      priceListId: get($customPriceListId) ?? '',
      priceListType: 'Custom' as const,
      ...(asOfStr ? { asOf: asOfStr } : {}),
    },
    skip: !get($customPriceListId),
  };
});

export const $isCustomPriceListLoading = atom((get) => {
  const data = get($remoteCustomPriceList);
  return data?.isLoading ?? false;
});

export const $customPriceListError = atom((get) => {
  const data = get($remoteCustomPriceList);
  return data?.error ?? null;
});

export const $remoteEvaluatedFormulas = evaluateFormulasQueryAtom((get) => {
  const priceListId = get($customPriceListId);
  const asOfDateTime = get($asOfDateTime);
  const isHistorical =
    asOfDateTime && asOfDateTime.compare(now(getLocalTimeZone())) < 0;

  return {
    variables: {
      priceListId: priceListId ?? '',
      priceListType: 'Custom' as PriceListType,
      ...(isHistorical ? { asOf: asOfDateTime.toAbsoluteString() } : {}),
    },
    skip: !priceListId,
  };
});

export const $evaluatedPrices = atom<EvaluatedPricesMap>((get) => {
  const data = get($remoteEvaluatedFormulas);
  return data?.data ?? {};
});

export const $selectedCell = atom<SelectedCell | null>(null);

export const $pendingTiersEffectiveAt = atom<
  import('@internationalized/date').CalendarDate | null | undefined
>(undefined);

export const $publishCustomPriceList = customPriceListPublishAtom({
  onSuccess: (get) => {
    get($remoteCustomPriceList).refetch?.();
    get($remoteProductPricings).refetch?.();
    get($remoteEvaluatedFormulas).refetch?.();
  },
});

export const $archiveCustomPriceList = customPriceListArchiveAtom({
  onSuccess: (get) => {
    get($remoteCustomPriceList).refetch?.();
  },
});

export const $unarchiveCustomPriceList = customPriceListUnarchiveAtom({
  onSuccess: (get) => {
    get($remoteCustomPriceList).refetch?.();
  },
});

export const $deleteCustomPriceList = customPriceListDeleteAtom({});
